import { createContext, useContext } from 'react'
import {
  useGetFormV2ForSettingsPageQuery,
  useGetSimpleFormQuery,
  useGetWorkflowsByFormQuery,
} from '~/common/generated/apollo-operations'
import { DetailedFormV2ForSettingsPage, FormWorkflow, SimpleForm } from './types'

type FormSettingsTabContextValue =
  | {
      form: SimpleForm | null
      formName: string
      isFormV2: false
      workflows: FormWorkflow[]
    }
  | {
      form: DetailedFormV2ForSettingsPage | null
      formName: string
      isFormV2: true
      workflows: FormWorkflow[]
    }

export function FormSettingsTabProvider({
  children,
  formId,
}: {
  children: React.ReactNode
  formId: string
}) {
  const { data: dataV1 } = useGetSimpleFormQuery({
    variables: { formId },
  })

  const { data: dataV2 } = useGetFormV2ForSettingsPageQuery({
    variables: { formId },
  })

  const { data: workflowsByForm } = useGetWorkflowsByFormQuery({ variables: { formId } })

  // Determine form version from data (prefer V2 if both exist)
  const isFormV2 = !!dataV2?.formV2

  const contextValue: FormSettingsTabContextValue = isFormV2
    ? {
        isFormV2: true,
        form: dataV2.formV2,
        formName: dataV2.formV2.name,
        workflows: workflowsByForm?.workflowsByForm ?? [],
      }
    : {
        isFormV2: false,
        form: dataV1?.form ?? null,
        formName: dataV1?.form.name ?? '',
        workflows: workflowsByForm?.workflowsByForm ?? [],
      }

  return (
    <FormSettingsTabContext.Provider value={contextValue}>
      {children}
    </FormSettingsTabContext.Provider>
  )
}

export const useFormTabSettings = () => {
  const context = useContext(FormSettingsTabContext)

  if (!context) {
    throw new Error('useFormTabSettings must be used within a FormSettingsTabProvider')
  }

  return context
}

const FormSettingsTabContext = createContext<FormSettingsTabContextValue | null>(null)
